import {
  ApiClientError,
  type AccountKind,
  type BillingTier,
  type BillingTierFilter,
  type ControlPlanePortfolioQuery,
  type ControlPlaneSnapshotResponse,
  type DeploymentAggregateResponse,
  type DeploymentModelsResponse,
  type DeploymentOperationsResponse,
  type DeploymentRoleModelResponse,
  type DeploymentServedModelResponse,
  type PortfolioMetricsResponse,
  type ProjectAggregateResponse,
  type TrendMetricResponse,
  type UsageVolumeResponse,
  type WorkspaceAggregateResponse,
} from '@contract-analyzer/api-client';

import { apiClient } from '@/lib/api';

export const controlPlaneDemoEnabled =
  import.meta.env.DEV && import.meta.env['VITE_CONTROL_PLANE_DEMO'] === 'true';

// The contract owns every wire shape; these aliases only give the UI shorter names.
export type ControlPlaneSnapshot = ControlPlaneSnapshotResponse;
export type PortfolioMetrics = PortfolioMetricsResponse;
export type DeploymentAggregate = DeploymentAggregateResponse;
export type DeploymentOperations = DeploymentOperationsResponse;
export type DeploymentModels = DeploymentModelsResponse;
export type DeploymentRoleModel = DeploymentRoleModelResponse;
export type DeploymentServedModel = DeploymentServedModelResponse;
export type WorkspaceAggregate = WorkspaceAggregateResponse;
export type ProjectAggregate = ProjectAggregateResponse;
export type TrendMetric = TrendMetricResponse;
export type UsageVolume = UsageVolumeResponse;
export type { AccountKind };

export type ControlPlanePeriod = NonNullable<ControlPlanePortfolioQuery['period']>;
export type StatusFilter = NonNullable<ControlPlanePortfolioQuery['status']>;
export type UsageCategory = NonNullable<ControlPlanePortfolioQuery['usage_category']>;
export type UsageCategoryFilter = UsageCategory | 'all';
export type CollectionState = DeploymentAggregate['collectionState'];
export type QueueStatus = NonNullable<DeploymentOperations['queueStatus']>;
export type { BillingTier, ControlPlanePortfolioQuery };

/** `'all'` means "do not narrow", so it is never sent to the API. */
export interface ControlPlaneFilters {
  readonly period: ControlPlanePeriod;
  readonly deployment: string;
  readonly workspace: string;
  readonly project: string;
  readonly chargeCode: string;
  readonly status: StatusFilter;
  readonly billingTier: BillingTierFilter;
  readonly usageCategory: UsageCategoryFilter;
}

export class ControlPlaneDataError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ControlPlaneDataError';
  }
}

/** The API query for a filter set; `'all'` filters are omitted, so equal queries compare equal. */
export function toPortfolioQuery(filters: ControlPlaneFilters): ControlPlanePortfolioQuery {
  return {
    period: filters.period,
    ...(filters.deployment === 'all' ? {} : { deployment: filters.deployment }),
    ...(filters.workspace === 'all' ? {} : { workspace: filters.workspace }),
    ...(filters.project === 'all' ? {} : { project: filters.project }),
    ...(filters.chargeCode === 'all' ? {} : { charge_code: filters.chargeCode }),
    ...(filters.status === 'all' ? {} : { status: filters.status }),
    ...(filters.billingTier === 'all' ? {} : { billing_tier: filters.billingTier }),
    ...(filters.usageCategory === 'all' ? {} : { usage_category: filters.usageCategory }),
  };
}

export async function loadControlPlaneSnapshot(
  filters: ControlPlaneFilters,
  signal?: AbortSignal,
): Promise<ControlPlaneSnapshot> {
  if (controlPlaneDemoEnabled) {
    const { createControlPlaneDemoSnapshot } = await import('@/lib/control-plane-demo');
    return createControlPlaneDemoSnapshot(filters);
  }

  try {
    return await apiClient.getControlPlanePortfolio(toPortfolioQuery(filters), {
      ...(signal === undefined ? {} : { signal }),
    });
  } catch (error: unknown) {
    if (error instanceof ApiClientError && (error.status === 401 || error.status === 403)) {
      throw new ControlPlaneDataError(
        'Production superadmin authorization could not be established.',
      );
    }
    throw new ControlPlaneDataError('The portfolio snapshot could not be loaded.');
  }
}
